import { StandardEvents } from '@shopify/events';

/**
 * Native selling-plan selector for the Cheribundi PDP.
 *
 * The buy box is two mode cards — "Spar-Abo" (subscription) and "Einmalkauf"
 * (one-time). The subscription card holds a delivery-interval picker. Each
 * option's price is rendered server side for every variant and only toggled
 * client side, so no money formatting happens in JavaScript. The only cart
 * side effect is the hidden `selling_plan` input, associated with Horizon's
 * product form through the `form` attribute and therefore picked up by
 * `new FormData(form)` in `product-form.js`. A `documentElement` flag lets the
 * variant-tiles block reveal its subscription per-portion prices with CSS.
 */
class CheribundiSellingPlans extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLInputElement | null} */
    this.sellingPlanInput = this.querySelector('[data-cheribundi-selling-plan-input]');
    this.section = this.closest('.shopify-section, dialog');

    this.addEventListener('change', this.handleChange);
    this.section?.addEventListener(StandardEvents.productSelect, this.handleProductSelect);

    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.section?.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
  }

  /** @param {Event} event */
  handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const role = event.target.dataset.cheribundiRole;
    if (role === 'mode' || role === 'freq') this.sync();
  };

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const variantId = detail?.resource?.id;
        if (!variantId) return;

        this.dataset.selectedVariantId = String(variantId);
        this.sync();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cheribundi-selling-plans] Event promise rejected:', error);
      });
  };

  /** @returns {string} 'subscription' | 'one-time'. */
  get mode() {
    const el = this.querySelector('[data-cheribundi-role="mode"]:checked');
    return el instanceof HTMLInputElement ? el.dataset.cheribundiMode ?? 'one-time' : 'one-time';
  }

  /** @returns {string} The selected delivery interval's selling plan id. */
  get sellingPlanId() {
    const el = this.querySelector('[data-cheribundi-role="freq"]:checked');
    return el instanceof HTMLInputElement ? el.dataset.cheribundiPlan ?? '' : '';
  }

  /**
   * Reflects the current variant / mode / interval selection in the DOM and in
   * the hidden `selling_plan` input.
   */
  sync() {
    const { mode, sellingPlanId } = this;
    const variantId = this.dataset.selectedVariantId ?? '';
    const isSubscription = mode === 'subscription';

    // Every price node shows itself only for the selected variant.
    for (const node of this.querySelectorAll('[data-cheribundi-variant]')) {
      if (!(node instanceof HTMLElement)) continue;
      node.hidden = node.dataset.cheribundiVariant !== variantId;
    }

    this.dataset.mode = mode;

    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = isSubscription ? sellingPlanId : '';
      this.sellingPlanInput.disabled = !isSubscription || !sellingPlanId;
    }

    // Lets the cheribundi-variant-tiles block reveal its subscription
    // per-portion prices with CSS only.
    document.documentElement.dataset.cheribundiSellingPlan = isSubscription ? 'true' : 'false';
  }
}

if (!customElements.get('cheribundi-selling-plans')) {
  customElements.define('cheribundi-selling-plans', CheribundiSellingPlans);
}

export default CheribundiSellingPlans;
